import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, HelpCircle } from 'lucide-react';
import type { ChatMessage } from '@/types';
import type { TeamMember } from '@/types';
import { CharacterPortrait } from '@/tools/inner-team/components/team/CharacterPortrait';
import { TypingIndicator } from './TypingIndicator';

interface TeamBlock {
  type: 'member' | 'synthesis' | 'followup';
  memberName?: string;
  content: string;
}

/**
 * Finds the synthesis separator index.
 * Prefers the --- that is immediately followed by **Synthese:** (exact boundary).
 * Falls back to the first --- when Synthese hasn't been written yet (mid-stream).
 */
function findSynthSep(text: string): number {
  const explicit = text.search(/\n\s*-{3,}\s*\n\s*\*\*Synthese:/i);
  if (explicit >= 0) return explicit;
  const first = text.match(/\n\s*-{3,}\s*\n/);
  return first?.index ?? -1;
}

/**
 * Member header regex: matches **Name (Role):** or **Name (Role)**: (colon inside or outside bold).
 * Requires parenthesised role to avoid matching **Synthese:** etc.
 */
const MEMBER_HEADER_RE = /\*\*([^*()\n]+?)\s*\([^)\n]+?\)\s*(?::\*\*|\*\*\s*:)/g;

/** Extract blocks that are fully complete (a next header or separator follows them). */
function extractCompleteBlocks(rawText: string): TeamBlock[] {
  const text = rawText.replace(/\r\n/g, '\n');
  const synthSepIdx = findSynthSep(text);
  const memberSection = synthSepIdx >= 0 ? text.slice(0, synthSepIdx) : text;

  const headers = [...memberSection.matchAll(MEMBER_HEADER_RE)];
  const blocks: TeamBlock[] = [];

  for (let i = 0; i < headers.length; i++) {
    const isLast = i === headers.length - 1;
    const start = headers[i].index! + headers[i][0].length;
    const end = isLast ? memberSection.length : headers[i + 1].index!;
    // Complete = not the last one being written, OR synthesis separator already found
    if (!isLast || synthSepIdx >= 0) {
      blocks.push({
        type: 'member',
        memberName: headers[i][1].trim(),
        content: memberSection.slice(start, end).trim(),
      });
    }
  }

  return blocks;
}

/** Splits text after the synthesis separator into { synthesis, followup }. */
function extractSynthParts(text: string, synthSepIdx: number): { synthesis: string; followup?: string } {
  const afterSep = text
    .slice(synthSepIdx)
    .replace(/^\n\s*-{3,}\s*\n/, '')
    .replace(/^\*\*Synthese:\*\*\s*/i, '');
  const trailIdx = afterSep.search(/\n\s*-{3,}\s*\n/);
  if (trailIdx >= 0) {
    const followup = afterSep.slice(trailIdx).replace(/^\n\s*-{3,}\s*\n/, '').trim();
    return { synthesis: afterSep.slice(0, trailIdx).trim(), followup: followup || undefined };
  }
  return { synthesis: afterSep.trim() };
}

/** Full parse including synthesis + follow-up — only call when streaming is done. */
function parseFullResponse(text: string): TeamBlock[] | null {
  const blocks = extractCompleteBlocks(text + '\n---\n'); // force all members complete
  const synthSepIdx = findSynthSep(text);
  if (synthSepIdx >= 0) {
    const { synthesis, followup } = extractSynthParts(text, synthSepIdx);
    if (synthesis) blocks.push({ type: 'synthesis', content: synthesis });
    if (followup) blocks.push({ type: 'followup', content: followup });
  }
  return blocks.length > 0 ? blocks : null;
}

interface SynthReveal {
  messageId: string;
  content: string;
  followup?: string;
  visible: boolean;
  followupVisible: boolean;
}

interface Props {
  messages: ChatMessage[];
  isStreaming: boolean;
  sessionMembers: TeamMember[];
  greeting: ChatMessage;
}

const INTER_BUBBLE_DELAY = 1400; // ms minimum between bubble reveals

export function TeamChatWindow({ messages, isStreaming, sessionMembers, greeting }: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const prevStreamingRef = useRef(false);
  const [synthReveal, setSynthReveal] = useState<SynthReveal | null>(null);

  // Staggered reveal state for streaming bubbles
  const [streamRevealCount, setStreamRevealCount] = useState(0);
  const nextRevealTargetRef = useRef(0);
  const lastRevealTimeRef = useRef(0);
  const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track whether user is near the bottom
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      isNearBottomRef.current = scrollHeight - scrollTop - clientHeight < 120;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll to bottom only when user is already near the bottom
  useEffect(() => {
    if (isNearBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isStreaming, synthReveal, streamRevealCount]);

  // When streaming ends: set up synthesis reveal with typing delay
  useEffect(() => {
    if (prevStreamingRef.current && !isStreaming) {
      const last = [...messages].reverse().find((m) => m.role === 'assistant');
      if (last?.content) {
        const synthSepIdx = findSynthSep(last.content);
        if (synthSepIdx >= 0) {
          const { synthesis, followup } = extractSynthParts(last.content, synthSepIdx);
          if (synthesis) {
            setSynthReveal({ messageId: last.id, content: synthesis, followup, visible: false, followupVisible: false });
            const t1 = setTimeout(() => setSynthReveal((s) => s && { ...s, visible: true }), 800);
            const t2 = setTimeout(() => setSynthReveal((s) => s && { ...s, followupVisible: true }), 1600);
            return () => { clearTimeout(t1); clearTimeout(t2); };
          }
        }
      }
    }
    prevStreamingRef.current = isStreaming;
  }, [isStreaming, messages]);

  // Reset stagger state when new stream starts
  useEffect(() => {
    if (isStreaming) {
      setSynthReveal(null);
      if (revealTimerRef.current) { clearTimeout(revealTimerRef.current); revealTimerRef.current = null; }
      nextRevealTargetRef.current = 0;
      lastRevealTimeRef.current = 0;
      setStreamRevealCount(0);
    }
  }, [isStreaming]);

  // Schedule staggered bubble reveals during streaming
  useEffect(() => {
    if (!isStreaming) return;
    const streamMsg = [...messages].reverse().find((m) => m.role === 'assistant');
    const complete = extractCompleteBlocks(streamMsg?.content ?? '');
    const newTarget = complete.length;

    if (newTarget <= nextRevealTargetRef.current) return;
    nextRevealTargetRef.current = newTarget;
    if (revealTimerRef.current) return; // already a timer in flight; it'll use latest target

    const elapsed = lastRevealTimeRef.current ? Date.now() - lastRevealTimeRef.current : INTER_BUBBLE_DELAY;
    const delay = Math.max(0, INTER_BUBBLE_DELAY - elapsed);

    revealTimerRef.current = setTimeout(() => {
      revealTimerRef.current = null;
      lastRevealTimeRef.current = Date.now();
      setStreamRevealCount(nextRevealTargetRef.current);
    }, delay);
  }, [isStreaming, messages]);

  // Clean up timer on unmount
  useEffect(() => () => { if (revealTimerRef.current) clearTimeout(revealTimerRef.current); }, []);

  const displayMessages = messages.length === 0 ? [greeting] : messages;
  const lastAssistantId =
    [...displayMessages].reverse().find((m) => m.role === 'assistant')?.id ?? null;

  function findMember(name: string): TeamMember | undefined {
    const lower = name.toLowerCase();
    return sessionMembers.find(
      (m) => m.name.toLowerCase() === lower || lower.includes(m.name.toLowerCase()),
    );
  }

  function renderBlock(block: TeamBlock, key: string) {
    if (block.type === 'synthesis') {
      return (
        <motion.div
          key={key}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-start px-4"
        >
          <div className="flex items-center gap-1.5 mb-1.5 ml-1">
            <Sparkles size={12} className="text-primary" />
            <span className="text-xs font-semibold text-primary">Synthese</span>
          </div>
          <div className="max-w-[82%] rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap bg-primary/5 border border-primary/20 text-foreground">
            {block.content}
          </div>
        </motion.div>
      );
    }

    if (block.type === 'followup') {
      return (
        <motion.div
          key={key}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-start px-4"
        >
          <div className="flex items-center gap-1.5 mb-1.5 ml-1">
            <HelpCircle size={12} className="text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground">Anschlussfrage</span>
          </div>
          <div className="max-w-[82%] rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap bg-muted/50 border border-border text-foreground">
            {block.content}
          </div>
        </motion.div>
      );
    }

    const member = block.memberName ? findMember(block.memberName) : undefined;
    return (
      <motion.div
        key={key}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col items-start px-4"
      >
        {member && (
          <div className="flex items-center gap-1.5 mb-1.5 ml-1">
            <CharacterPortrait
              emoji={member.avatarEmoji}
              color={member.avatarColor}
              avatarImageUrl={member.avatarImageUrl}
              size="xs"
            />
            <span className="text-xs font-semibold text-foreground/80">{member.name}</span>
            <span className="text-xs text-muted-foreground">· {member.role}</span>
          </div>
        )}
        <div className="max-w-[82%] rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap bg-card border border-border text-foreground">
          {block.content}
        </div>
      </motion.div>
    );
  }

  function renderAssistant(msg: ChatMessage) {
    const isCurrentStream = isStreaming && msg.id === lastAssistantId;

    // Currently streaming: show staggered complete blocks up to streamRevealCount
    if (isCurrentStream) {
      const complete = extractCompleteBlocks(msg.content ?? '');
      const visible = complete.slice(0, streamRevealCount);
      if (visible.length === 0) return <div key={msg.id} />;
      return (
        <div key={msg.id} className="space-y-3 pb-1">
          {visible.map((block, i) => renderBlock(block, `${msg.id}-${i}`))}
        </div>
      );
    }

    // This message just finished streaming → show member blocks + staged reveal
    if (synthReveal?.messageId === msg.id) {
      const memberBlocks = extractCompleteBlocks(msg.content ?? '');
      return (
        <div key={msg.id} className="space-y-3 pb-1">
          {memberBlocks.map((block, i) => renderBlock(block, `${msg.id}-${i}`))}
          {!synthReveal.visible ? (
            <TypingIndicator />
          ) : (
            <>
              {renderBlock({ type: 'synthesis', content: synthReveal.content }, `${msg.id}-synth`)}
              {synthReveal.followup && !synthReveal.followupVisible && <TypingIndicator />}
              {synthReveal.followup && synthReveal.followupVisible &&
                renderBlock({ type: 'followup', content: synthReveal.followup }, `${msg.id}-followup`)}
            </>
          )}
        </div>
      );
    }

    // History message: parse and show all at once
    const blocks = msg.content ? parseFullResponse(msg.content) : null;
    if (!blocks) {
      return (
        <motion.div
          key={msg.id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="flex flex-col items-start px-4 pb-2"
        >
          <div className="max-w-[82%] rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap bg-card border border-border text-foreground">
            {msg.content || <span className="text-muted-foreground italic">…</span>}
          </div>
        </motion.div>
      );
    }
    return (
      <div key={msg.id} className="space-y-3 pb-1">
        {blocks.map((block, i) => renderBlock(block, `${msg.id}-${i}`))}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto py-4 space-y-1">
      {displayMessages.map((msg) => {
        if (msg.role === 'user') {
          return (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="flex flex-col items-end px-4 pb-2"
            >
              <div className="max-w-[82%] rounded-2xl rounded-br-sm px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap bg-primary text-primary-foreground">
                {msg.content}
              </div>
            </motion.div>
          );
        }
        return renderAssistant(msg);
      })}
      {isStreaming && <TypingIndicator />}
      <div ref={bottomRef} />
    </div>
  );
}
